import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';

import LoginButton from '../../components/login-button';
import { VIEW_MODE_SITE } from '../../constants/view-modes';
import * as MP from '../../state/members-page/actions';
import AnimatedLoader from '../../components/animated-loader';
import LoadMore from '../../components/load-more';
import MembersList from '../../components/member-list';
import MembersPlaceholder from '../../components/members-placeholder';
import NoMemberResults from '../../components/no-member-results';
import Page from '../../components/page';
import MemberHeader from '../../components/member-header';
import { getComponentSettings } from '../../selectors/settings-selectors';
import withTranslate from '../../hoc/with-translate';
import EmptyStates from '../../components/empty-states';
import styles from './members-page.scss';
import * as navigation from '../../state/navigation/actions';
import { isBlocked as isMemberBlocked, getCurrentMemberId, getCurrentMember } from '../../state/members/selectors';
import {
  getMembersCount,
  getDisplayMembersCount,
  getMembersLayout,
  getMembersIdList,
  isMembersLoading,
  getQuery,
  getBadge,
  getPage,
} from '../../state/members-page/selectors';
import {
  getVisibileFilters,
  getSorts,
  getIsMobile,
  getViewMode,
  isExperimentEnabled,
  getIsRtlLocale,
  getAllExperiments,
  isDemoMode as isDemoModeSelector,
} from '../../state/site/selectors';
import { Experiments } from '../../constants/experiments';
import { openInfoNotification } from '../../actions/open-modal';
import { MEMBERS_PER_PAGE } from '../../constants/pagination';

// TODO: refactor to stateless component
export class MembersPage extends React.Component {
  renderGuest = () => {
    const { t } = this.props;
    return (
      <EmptyStates
        className={styles.container}
        title={t('login.members-page.title')}
        content={t('login.members-page.text')}
        noBoxClass
      >
        <LoginButton t={t} />
      </EmptyStates>
    );
  };

  renderNone = (name, viewMode) => {
    return viewMode !== VIEW_MODE_SITE ? <MembersPlaceholder name={name} /> : <NoMemberResults name={name} />;
  };

  renderList = () => {
    const {
      totalMembersCount,
      fetchMembers,
      page,
      membersIds,
      goToFFPage,
      goToMemberPage,
      pageLayout,
      onRoleAction,
      // eslint-disable-next-line react/prop-types
      haveMembers,
      isLoading,
    } = this.props;
    return (
      <LoadMore
        loadMore={() => fetchMembers(page + 1)}
        hasMore={page * MEMBERS_PER_PAGE < totalMembersCount}
        isLoading={isLoading && !haveMembers}
        isMobile={this.props.isMobile}
      >
        <MembersList
          membersIds={membersIds}
          goToFFPage={goToFFPage}
          goToMemberPage={goToMemberPage}
          layoutStyle={pageLayout}
          onRoleAction={onRoleAction}
        />
      </LoadMore>
    );
  };

  renderListOrNone = () => {
    const { isLoading, membersIds, query, viewMode } = this.props;
    const haveMembers = membersIds && membersIds.length > 0;

    return (
      <AnimatedLoader isLoading={isLoading && !haveMembers}>
        {haveMembers ? this.renderList() : this.renderNone(query.search, viewMode)}
      </AnimatedLoader>
    );
  };

  renderGuestOrLoader = () => {
    const { currentMember, componentSettings, isDemoMode } = this.props;

    if (isDemoMode) {
      return this.renderGuest();
    }

    if (currentMember || componentSettings.guestsAllowed) {
      return this.renderListOrNone();
    }

    return this.renderGuest();
  };

  render = () => {
    const {
      totalMembersCount,
      displayTotalMembersCount,
      onSetMembersLayout,
      query,
      filters,
      sorts,
      pageLayout,
      isMobile,
      setMembersSortAndFetch,
      setMembersFilterAndFetch,
      setMembersSearchAndFetch,
      clearMembersBadgeIdAndFetch,
      isRtlLocale,
      badge,
      // eslint-disable-next-line react/prop-types
      shouldShowMembersWithBadge,
      displayMemberSortField,
    } = this.props;
    return (
      <Page noSpacing styleClass="ma-all-members-page-bg">
        <div className={classNames(isMobile ? styles.mobileContainer : styles.desktopContainer)}>
          <MemberHeader
            sort={query.sort}
            sorts={sorts}
            search={query.search}
            filter={query.filter}
            filters={filters}
            onSortChange={setMembersSortAndFetch}
            onSearchChange={setMembersSearchAndFetch}
            onViewChange={(type) => () => onSetMembersLayout(type)}
            onFilterChange={setMembersFilterAndFetch}
            membersCount={totalMembersCount}
            displayMembersCount={displayTotalMembersCount}
            badge={shouldShowMembersWithBadge && badge}
            onBadgeRemove={clearMembersBadgeIdAndFetch}
            pageLayout={pageLayout}
            isRtlLocale={isRtlLocale}
            displayMemberSortField={displayMemberSortField}
          />
          {this.renderGuestOrLoader()}
        </div>
      </Page>
    );
  };
}

MembersPage.propTypes = {
  fetchMembers: PropTypes.func.isRequired,
  sorts: PropTypes.object,
  query: PropTypes.object.isRequired,
  filters: PropTypes.object,
  membersIds: PropTypes.array,
  totalMembersCount: PropTypes.number,
  page: PropTypes.number,
  displayTotalMembersCount: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  viewMode: PropTypes.string.isRequired,
  componentSettings: PropTypes.object.isRequired,
  currentMember: PropTypes.object,
  onFilterChange: PropTypes.func,
  pageLayout: PropTypes.number,
  onSetMembersLayout: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  clearMembers: PropTypes.func,
  setMembersSortAndFetch: PropTypes.func.isRequired,
  setMembersFilterAndFetch: PropTypes.func.isRequired,
  setMembersSearchAndFetch: PropTypes.func.isRequired,
  clearMembersBadgeIdAndFetch: PropTypes.func.isRequired,
  goToFFPage: PropTypes.func,
  goToMemberPage: PropTypes.func,
  isBlocked: PropTypes.bool,
  onRoleAction: PropTypes.func,
  isRtlLocale: PropTypes.bool,
  badge: PropTypes.object,
  experiments: PropTypes.object,
  isDemoMode: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  membersIds: getMembersIdList(state),
  filters: getVisibileFilters(state),
  sorts: getSorts(state),
  query: getQuery(state),
  totalMembersCount: getMembersCount(state),
  page: getPage(state),
  displayTotalMembersCount: getDisplayMembersCount(state),
  isLoading: isMembersLoading(state),
  viewMode: getViewMode(state),
  badge: getBadge(state),
  componentSettings: getComponentSettings(state),
  currentMember: getCurrentMember(state),
  pageLayout: getMembersLayout(state),
  isBlocked: isMemberBlocked(state, getCurrentMemberId(state)),
  isMobile: getIsMobile(state),
  shouldShowMembersWithBadge: isExperimentEnabled(state, Experiments.SHOW_MORE_MEMBERS_WITH_BADGE),
  isRtlLocale: getIsRtlLocale(state),
  experiments: getAllExperiments(state),
  isDemoMode: isDemoModeSelector(state),
  displayMemberSortField: !isExperimentEnabled(state, Experiments.HIDE_MEMBER_SORT_FIELD),
});

const mapDispatchToProps = {
  fetchMembers: MP.fetchMembers,
  clearMembers: MP.clearMembers,
  onSetMembersLayout: MP.onSetMembersLayout,
  setMembersSortAndFetch: MP.setMembersSortAndFetch,
  setMembersFilterAndFetch: MP.setMembersFilterAndFetch,
  setMembersSearchAndFetch: MP.setMembersSearchAndFetch,
  clearMembersBadgeIdAndFetch: MP.clearMembersBadgeIdAndFetch,
  goToFFPage: navigation.goToFFPage,
  goToMemberPage: navigation.goToMemberPage,
  onRoleAction: MP.onRoleAction,
  showNotification: openInfoNotification,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslate(MembersPage));
